import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";

const providerLabel = {
  resend: { label: "Resend", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  smtp: { label: "SMTP", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  dev: { label: "Dev (link no log)", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

export default function AdminEmail() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Teste de envio — CEAP BRASIL");
  const [body, setBody] = useState("Este é um e-mail de teste enviado pelo painel administrativo do CEAP BRASIL.");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/email/status").then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!to) { toast.error("Informe um e-mail destinatário"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/email/test", { to, subject, body });
      if (data.provider === "dev") {
        toast.info("Provedor não configurado. E-mail registrado nos logs do servidor (modo dev).");
      } else if (data.provider === "error") {
        toast.error("Falha no envio: " + (data.error || "erro desconhecido"));
      } else {
        toast.success(`E-mail de teste enviado via ${data.provider}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  return (
    <AdminLayout title="E-mail (recuperação de senha)">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-xl"><CardContent className="p-5 space-y-3">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] flex items-center gap-2"><Mail className="w-4 h-4" /> Configuração atual</h3>
          {!status ? <p className="text-sm text-muted-foreground">Carregando…</p> : (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] p-3">
                <p className="text-sm text-muted-foreground">Provedor ativo</p>
                <Badge className={providerLabel[status.provider]?.cls} variant="outline" data-testid="email-provider-badge">
                  {providerLabel[status.provider]?.label || status.provider}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg border border-[hsl(var(--border))] p-3">
                  <p className="text-xs text-muted-foreground">Resend</p>
                  <p className="flex items-center gap-1 mt-0.5">
                    {status.resend_configured ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
                    {status.resend_configured ? "Configurado" : "Não configurado"}
                  </p>
                </div>
                <div className="rounded-lg border border-[hsl(var(--border))] p-3">
                  <p className="text-xs text-muted-foreground">SMTP</p>
                  <p className="flex items-center gap-1 mt-0.5">
                    {status.smtp_configured ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
                    {status.smtp_configured ? "Configurado" : "Não configurado"}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-[hsl(var(--border))] p-3">
                <p className="text-xs text-muted-foreground">Remetente</p>
                <p className="text-sm font-medium">{status.sender_name} &lt;{status.sender_email}&gt;</p>
              </div>
              {status.provider === "dev" && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-800">
                  <p className="font-medium mb-1">Modo de desenvolvimento</p>
                  <p>Os e-mails NÃO são enviados — o link de redefinição é registrado nos logs do servidor.<br />
                    Configure <code className="font-mono-id">RESEND_API_KEY</code> ou <code className="font-mono-id">SMTP_HOST</code>/<code className="font-mono-id">SMTP_USER</code>/<code className="font-mono-id">SMTP_PASSWORD</code> no <code className="font-mono-id">/app/backend/.env</code> e reinicie o backend para habilitar o envio real.</p>
                </div>
              )}
            </div>
          )}
        </CardContent></Card>

        <Card className="rounded-xl"><CardContent className="p-5 space-y-3">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] flex items-center gap-2"><Send className="w-4 h-4" /> Enviar e-mail de teste</h3>
          <div><Label>Destinatário</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="voce@exemplo.com" data-testid="email-test-to" /></div>
          <div><Label>Assunto</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div><Label>Mensagem</Label><Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} /></div>
          <Button onClick={sendTest} disabled={loading || !to} className="w-full" data-testid="email-test-send">
            {loading ? "Enviando…" : "Enviar teste"}
          </Button>
          <p className="text-[11px] text-muted-foreground">Em modo Dev, o conteúdo é apenas registrado nos logs.</p>
        </CardContent></Card>
      </div>

      <Card className="rounded-xl mt-4"><CardContent className="p-5">
        <h3 className="font-display font-semibold text-[hsl(var(--primary))] mb-2">Como configurar</h3>
        <p className="text-sm text-muted-foreground mb-3">Edite <code className="font-mono-id text-[12px]">/app/backend/.env</code> e reinicie o backend (<code className="font-mono-id text-[12px]">supervisorctl restart backend</code>):</p>
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-3 text-xs font-mono-id whitespace-pre-wrap leading-relaxed">
{`# Opção 1 — Resend (recomendado)
RESEND_API_KEY="re_xxxxxxxxxxxxxxxxxxxxxxxx"
SENDER_NAME="CEAP BRASIL"
SENDER_EMAIL="naoresponda@ceapbrasil.com.br"   # domínio verificado no Resend

# Opção 2 — SMTP genérico (Gmail/Zoho/Outlook/servidor próprio)
SMTP_HOST="smtp.gmail.com"
SMTP_PORT="587"
SMTP_USER="seuemail@gmail.com"
SMTP_PASSWORD="senha-de-app"
SMTP_USE_TLS="true"
SMTP_USE_SSL="false"
SENDER_NAME="CEAP BRASIL"
SENDER_EMAIL="seuemail@gmail.com"`}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Crie sua API Key Resend em <a className="text-[hsl(var(--accent))] underline" href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer">resend.com/api-keys</a> (3.000 e-mails grátis/mês).</p>
      </CardContent></Card>
    </AdminLayout>
  );
}
